import React, { useState, useEffect } from 'react';
import { Settings, Map, Users, Plus, Grid3X3, Truck, Navigation2, DollarSign } from 'lucide-react';
import WarehouseMap from '../warehouse/WarehouseMap';
import WarehouseStructure from './WarehouseStructure';
import CompanyPartnershipsModal from './CompanyPartnershipsModal';
import WarehouseIncomingDeliveries from '../WarehouseIncomingDeliveries';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const WarehouseSettings = () => {
  const [activeTab, setActiveTab] = useState('structure');
  const [activeMapCompanyTab, setActiveMapCompanyTab] = useState(null);
  const [racks, setRacks] = useState([]);
  const [occupancyData, setOccupancyData] = useState({});
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationDetails, setLocationDetails] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    // Load from localStorage or default to EUR
    return localStorage.getItem('warehouse_currency') || 'EUR';
  });

  // Fetch warehouse data for map views
  useEffect(() => {
    if (activeTab === 'map') {
      console.log('WarehouseSettings - activeTab changed to map, fetching data...');
      fetchWarehouseData();
    }
  }, [activeTab]);

  // Function to refresh map data
  const refreshMapData = () => {
    console.log('WarehouseSettings - Manual refresh triggered');
    if (activeTab === 'map') {
      fetchWarehouseData();
    }
  };

  // Function to handle currency change
  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    localStorage.setItem('warehouse_currency', currency);

    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('currencyChanged', { detail: { currency } }));

    console.log('Currency changed to:', currency);
  };

  // Define available currencies
  const currencies = [
    { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'US Dollar', symbol: '$' }
  ];

  // Fetch location details when a location is selected
  useEffect(() => {
    if (selectedLocation && activeTab === 'map') {
      fetchLocationDetails(selectedLocation);
    } else {
      setLocationDetails(null);
    }
  }, [selectedLocation, activeTab]);

  const fetchLocationDetails = async (locationCode) => {
    if (!locationCode) return;

    setLoadingLocation(true);
    try {
      console.log('Fetching details for location:', locationCode);

      const response = await axios.get(`${API_BASE_URL}/api/warehouse/items/?location=${locationCode}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      console.log('Location details API response:', response.data);

      const items = response.data.results || response.data || [];

      setLocationDetails({
        location: locationCode,
        items: items,
        totalValue: items.reduce(
          (sum, item) => sum + ((item.quantity_in_stock || item.quantity || 0) * (item.unit_price || 0)), 0
        ),
        totalQuantity: items.reduce((sum, item) => sum + (item.quantity_in_stock || item.quantity || 0), 0)
      });
    } catch (error) {
      console.error('Error fetching location details:', error);
      console.error('Error response:', error.response?.data);

      setLocationDetails({
        location: locationCode,
        items: [],
        totalValue: 0,
        totalQuantity: 0
      });
    } finally {
      setLoadingLocation(false);
    }
  };

  const fetchWarehouseData = async () => {
    try {
      // Fetch racks structure
      const racksResponse = await axios.get(`${API_BASE_URL}/api/warehouse/structure/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const racksData = Array.isArray(racksResponse.data) ? racksResponse.data :
                       racksResponse.data.results ? racksResponse.data.results : [];
      setRacks(racksData);

      // Fetch all warehouse items to get complete occupancy data
      const itemsResponse = await axios.get(`${API_BASE_URL}/api/warehouse/items/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      console.log('WarehouseSettings - All items response:', itemsResponse.data);

      const items = itemsResponse.data.results || itemsResponse.data || [];
      const occupancyMap = {};
      let processedLocations = [];

      items.forEach((item, index) => {
        console.log(`WarehouseSettings - Processing item ${index}:`, {
          name: item.name || item.item_name,
          location: item.location,
          locations: item.locations
        });
        // Handle single location
        if (item.location) {
          // Handle locations stored as comma-separated string (multi-location items)
          if (item.location.includes(',')) {
            const locations = item.location.split(',').map(loc => loc.trim());
            locations.forEach(location => {
              if (location) {
                occupancyMap[location] = true;
                processedLocations.push(location);
                console.log(`WarehouseSettings - Added multi-location: ${location} for item: ${item.name || item.item_name}`);
              }
            });
          } else {
            // Single location
            occupancyMap[item.location] = true;
            processedLocations.push(item.location);
            console.log(`WarehouseSettings - Added single location: ${item.location} for item: ${item.name || item.item_name}`);
          }
        }

        // Handle multiple locations array (if item.locations exists)
        if (item.locations && Array.isArray(item.locations)) {
          item.locations.forEach(location => {
            if (location) {
              occupancyMap[location] = true;
              processedLocations.push(location);
              console.log(`WarehouseSettings - Added array location: ${location} for item: ${item.name || item.item_name}`);
            }
          });
        }
      });

      // Also fetch equipment locations
      try {
        const equipmentResponse = await axios.get(`${API_BASE_URL}/api/safetyequipment/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        console.log('WarehouseSettings - Equipment response:', equipmentResponse.data);

        const equipmentItems = equipmentResponse.data.results || equipmentResponse.data || [];
        equipmentItems.forEach((item, index) => {
          const location = item.safety_equipment_location;
          if (location && location !== 'Warehouse') {
            // Handle comma-separated locations
            if (location.includes(',')) {
              const locations = location.split(',').map(loc => loc.trim());
              locations.forEach(loc => {
                if (loc) {
                  occupancyMap[loc] = true;
                  processedLocations.push(loc);
                  console.log(`WarehouseSettings - Added equipment location: ${loc} for equipment: ${item.safety_equipment_type_name}`);
                }
              });
            } else {
              occupancyMap[location] = true;
              processedLocations.push(location);
              console.log(`WarehouseSettings - Added equipment location: ${location} for equipment: ${item.safety_equipment_type_name}`);
            }
          }
        });
      } catch (equipmentError) {
        console.error('Error fetching equipment data:', equipmentError);
      }

      console.log('WarehouseSettings - Complete occupancy map from items:', occupancyMap);
      console.log('WarehouseSettings - All processed locations:', processedLocations);
      console.log('WarehouseSettings - Total locations found:', Object.keys(occupancyMap).length);
      setOccupancyData(occupancyMap);

    } catch (error) {
      console.error('Error fetching warehouse data:', error);

      // Fallback to occupancy endpoint if items endpoint fails
      try {
        const occupancyResponse = await axios.get(`${API_BASE_URL}/api/warehouse/structure/occupancy/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const occupancyDataArray = Array.isArray(occupancyResponse.data) ? occupancyResponse.data :
                                  occupancyResponse.data.results ? occupancyResponse.data.results : [];
        const occupancyMap = {};
        occupancyDataArray.forEach(item => {
          occupancyMap[item.location] = true;
        });
        setOccupancyData(occupancyMap);
      } catch (fallbackError) {
        console.error('Error fetching occupancy data:', fallbackError);
      }
    }
  };

  const tabs = [
    { id: 'structure', label: 'Structure', icon: Grid3X3 },
    { id: 'map', label: 'Map', icon: Map },
    { id: 'partnerships', label: 'Company Partnerships', icon: Users },
    { id: 'incoming-deliveries', label: 'Incoming Deliveries', icon: Truck },
    { id: 'currency', label: 'Currency Settings', icon: DollarSign }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'structure':
        return <WarehouseStructure />;
      case 'map':
        return (
          <div className="bg-white rounded-xl shadow-sm border border-brand-200 p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 flex items-center">
                    <Map className="h-6 w-6 mr-2 text-brand-600" />
                    Warehouse Map
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    Interactive warehouse layout with location management and field selections
                  </p>
                </div>

                <div className="flex items-center space-x-3">

                  {/* Refresh Button */}
                  <button
                    onClick={refreshMapData}
                    className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
                    title="Refresh map data"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>


            {/* Map Content */}
            <div className="space-y-4">
              {/* Selected location info */}
              {selectedLocation && (
                <div className="bg-brand-50 border border-brand-200 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-brand-200 bg-brand-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-brand-700">
                        <Navigation2 className="h-5 w-5 mr-2" />
                        <div>
                          <div className="font-mono font-bold text-xl">{selectedLocation}</div>
                          {selectedLocation.includes('-') && (
                            <div className="text-sm text-brand-600 mt-1">
                              Rack: {selectedLocation.split('-')[0]} |
                              Shelf: {selectedLocation.split('-')[1]} |
                              Container: {selectedLocation.split('-')[2]}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedLocation('')}
                        className="px-3 py-2 text-sm text-brand-600 hover:text-brand-800 hover:bg-brand-200 rounded border border-brand-200 transition-colors"
                      >
                        Clear Selection
                      </button>
                    </div>
                  </div>

                  {/* Location Details Panel */}
                  <div className="p-4">
                    {loadingLocation ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
                        <p className="text-slate-600 mt-2">Loading location details...</p>
                      </div>
                    ) : locationDetails ? (
                      <div className="space-y-4">
                        {locationDetails.items.length > 0 ? (
                          <div>
                            <h4 className="font-semibold text-slate-900 mb-3">Items at this location:</h4>
                            <div className="space-y-4">
                              {locationDetails.items.map((item, index) => (
                                <div key={index} className="bg-white border border-brand-200 rounded-xl p-4 shadow-sm">
                                  {/* Item Name */}
                                  <div className="text-lg font-bold text-slate-900 mb-2">
                                    {item.name || item.item_name || 'Testowy produkt'}
                                  </div>

                                  {/* Last Updated */}
                                  <div className="text-sm text-slate-600 mb-2">
                                    Last updated: {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : new Date().toLocaleDateString()}
                                  </div>

                                  {/* Category */}
                                  <div className="text-sm font-medium text-slate-800 mb-1">
                                    {item.category || 'Equipment'}
                                  </div>

                                  {/* Quantity */}
                                  <div className="text-sm text-slate-700 mb-1">
                                    {item.quantity_in_stock || item.quantity || 111} pcs
                                  </div>

                                  {/* Minimum Stock */}
                                  <div className="text-sm text-slate-700 mb-1">
                                    Min: {item.minimum_stock_level || 2} pcs
                                  </div>

                                  {/* Status */}
                                  <div className={`text-sm font-medium mb-1 ${
                                    (item.quantity_in_stock || item.quantity || 0) > (item.minimum_stock_level || 0)
                                      ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {(item.quantity_in_stock || item.quantity || 0) > (item.minimum_stock_level || 0)
                                      ? 'in stock' : 'low stock'}
                                  </div>

                                  {/* Company/Supplier */}
                                  <div className="text-sm text-slate-700 mb-1">
                                    {item.supplier || item.company || 'Omega'}
                                  </div>

                                  {/* Visibility */}
                                  <div className="text-sm text-slate-700 mb-3">
                                    {item.visibility || 'Private'}
                                  </div>

                                  {/* Locations */}
                                  <div className="text-sm font-mono text-brand-600 border-t border-brand-200 pt-3">
                                    <span className="font-medium text-slate-700">Location: </span>
                                    <div className="font-mono text-brand-600 font-bold mt-1">
                                      {selectedLocation}
                                    </div>
                                  </div>

                                  {/* Additional Details */}
                                  {item.sku && (
                                    <div className="text-xs text-slate-500 mt-2">
                                      SKU: {item.sku}
                                    </div>
                                  )}
                                  {item.unit_price && (
                                    <div className="text-xs text-slate-500">
                                      Unit Price: €{item.unit_price}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Summary Statistics */}
                            <div className="mt-6 grid grid-cols-2 gap-4 bg-steel-50 p-4 rounded-xl">
                              <div className="text-center">
                                <div className="text-lg font-bold text-slate-900">{locationDetails.items.length}</div>
                                <div className="text-sm text-slate-600">Item Types</div>
                              </div>
                              <div className="text-center">
                                <div className="text-lg font-bold text-slate-900">{locationDetails.totalQuantity}</div>
                                <div className="text-sm text-slate-600">Total Items</div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-slate-500">
                            <Grid3X3 className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                            <p className="text-lg font-medium">No items at this location</p>
                            <p className="text-sm">This location is available for new inventory</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <p>Select a location on the map to see details</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Map Display - Tabbed by Company */}
              {(() => {
                // Get current user's company name from localStorage or API
                const currentUserCompanyName = localStorage.getItem('user_company_name');

                // Group racks by company
                const racksByCompany = racks.reduce((acc, rack) => {
                  const companyName = rack.owner_company_name || (currentUserCompanyName || 'My Company');
                  if (!acc[companyName]) {
                    acc[companyName] = [];
                  }
                  acc[companyName].push(rack);
                  return acc;
                }, {});

                const companies = Object.keys(racksByCompany);

                // Set initial tab if not set
                if (companies.length > 0 && !activeMapCompanyTab) {
                  const myCompany = companies.find(c => c === currentUserCompanyName) || companies[0];
                  setActiveMapCompanyTab(myCompany);
                  return null; // Skip render while setting state
                }

                if (companies.length === 0) {
                  return (
                    <div className="text-center py-12 text-slate-500">
                      <p>No warehouse structure configured yet</p>
                    </div>
                  );
                }

                const activeCompanyRacks = activeMapCompanyTab ? racksByCompany[activeMapCompanyTab] || [] : [];
                const companyTotalLocations = activeCompanyRacks.reduce((sum, rack) => sum + (rack.shelves * rack.containers), 0);

                // Filter occupancy data for active company's racks
                const companyRackNames = activeCompanyRacks.map(r => r.name);
                const companyOccupancyData = Object.keys(occupancyData)
                  .filter(loc => companyRackNames.some(rackName => loc.startsWith(rackName + '-')))
                  .reduce((obj, key) => {
                    obj[key] = occupancyData[key];
                    return obj;
                  }, {});

                return (
                  <div>
                    {/* Company Tabs */}
                    <div className="mb-6">
                      <div className="border-b border-brand-200">
                        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                          {companies.map((companyName) => (
                            <button
                              key={companyName}
                              onClick={() => setActiveMapCompanyTab(companyName)}
                              className={`${
                                activeMapCompanyTab === companyName
                                  ? 'border-brand-500 text-brand-600'
                                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-brand-300'
                              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                            >
                              {companyName}
                              <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-steel-100">
                                {racksByCompany[companyName].length}
                              </span>
                            </button>
                          ))}
                        </nav>
                      </div>
                    </div>

                    {/* Active Company Map */}
                    <div className="border border-brand-200 rounded-xl overflow-hidden mb-4">
                      <WarehouseMap
                        selectedLocation={selectedLocation}
                        onLocationSelect={setSelectedLocation}
                        showOccupancy={true}
                        racks={activeCompanyRacks}
                        occupancyData={companyOccupancyData}
                      />
                    </div>

                    {/* Active Company Statistics */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-steel-50 p-4 rounded-xl">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-slate-900">{activeCompanyRacks.length}</div>
                        <div className="text-sm text-slate-600">Racks</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-slate-900">{companyTotalLocations}</div>
                        <div className="text-sm text-slate-600">Locations</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {Object.keys(companyOccupancyData).length}
                        </div>
                        <div className="text-sm text-slate-600">Occupied</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {companyTotalLocations - Object.keys(companyOccupancyData).length}
                        </div>
                        <div className="text-sm text-slate-600">Available</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        );
      case 'partnerships':
        return (
          <div className="bg-white rounded-xl shadow-sm border border-brand-200 p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center">
                <Users className="h-6 w-6 mr-2 text-brand-600" />
                Company Partnerships
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Manage partnerships and sharing relationships with other companies
              </p>
            </div>
            <CompanyPartnershipSettings />
          </div>
        );
      case 'incoming-deliveries':
        return (
          <div className="bg-white rounded-xl shadow-sm border border-brand-200 p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center">
                <Truck className="h-6 w-6 mr-2 text-brand-600" />
                Incoming Deliveries
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Manage incoming warehouse deliveries and shipments
              </p>
            </div>
            <WarehouseIncomingDeliveries />
          </div>
        );
      case 'currency':
        return (
          <div className="bg-white rounded-xl shadow-sm border border-brand-200 p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center">
                <DollarSign className="h-6 w-6 mr-2 text-brand-600" />
                Currency Settings
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Set the default currency for warehouse item prices and calculations
              </p>
            </div>

            <div className="max-w-2xl">
              {/* Current Currency Display */}
              <div className="mb-6 p-4 bg-brand-50 rounded-xl border border-brand-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-xl font-bold text-brand-600">
                        {currencies.find(c => c.code === selectedCurrency)?.symbol}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Current Currency</h3>
                      <p className="text-sm text-slate-600">
                        {currencies.find(c => c.code === selectedCurrency)?.name} ({selectedCurrency})
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Currency Selection */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Select Default Currency</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {currencies.map((currency) => (
                    <button
                      key={currency.code}
                      onClick={() => handleCurrencyChange(currency.code)}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                        selectedCurrency === currency.code
                          ? 'border-brand-500 bg-brand-50 shadow-md'
                          : 'border-brand-200 bg-white hover:border-brand-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl font-bold mb-2">
                          {currency.symbol}
                        </div>
                        <div className="text-lg font-semibold text-slate-900">
                          {currency.code}
                        </div>
                        <div className="text-sm text-slate-600">
                          {currency.name}
                        </div>
                        {selectedCurrency === currency.code && (
                          <div className="mt-2 text-xs text-brand-600 font-medium">
                            ✓ Selected
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Usage Information */}
              <div className="bg-steel-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-2">How it works:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• When adding new items, prices will default to {selectedCurrency}</li>
                  <li>• Existing items will keep their current currency</li>
                  <li>• This setting affects warehouse inventory forms</li>
                  <li>• Currency preference is saved locally on your browser</li>
                </ul>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 bg-steel-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900">
            Warehouse Settings
          </h1>
          <p className="text-slate-600 mt-1">
            Configure warehouse settings, manage structure, and set up partnerships
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-1 bg-steel-100 p-1 rounded-xl">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-steel-50'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

// Company Partnership Settings Component
const CompanyPartnershipSettings = () => {
  const [partnerships, setPartnerships] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchPartnerships();
    fetchAvailablePartners();
  }, []);

  const fetchPartnerships = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/company-access/accessible_companies/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setPartnerships(response.data || []);
    } catch (error) {
      console.error('Error fetching partnerships:', error);
      setPartnerships([]);
    }
  };

  const fetchAvailablePartners = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/company-access/available_partners/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setAvailablePartners(response.data || []);
    } catch (error) {
      console.error('Error fetching available partners:', error);
      setAvailablePartners([]);
    } finally {
      setLoading(false);
    }
  };

  const createPartnership = async () => {
    if (!selectedPartner) return;
    
    setCreating(true);
    try {
      await axios.post(`${API_BASE_URL}/api/warehouse/company-access/create_partnership/`, {
        partner_company_id: selectedPartner,
        access_level: 'full_access'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      alert('Partnership created successfully!');
      setSelectedPartner('');
      fetchPartnerships();
      fetchAvailablePartners();
    } catch (error) {
      console.error('Error creating partnership:', error);
      const errorMessage = error.response?.data?.error || 'Error creating partnership';
      alert(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const removePartnership = async (partnerId, partnerName) => {
    if (!window.confirm(`Remove partnership with ${partnerName}?`)) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/warehouse/company-access/remove_partnership/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        data: {
          partner_company_id: partnerId
        }
      });

      alert('Partnership removed successfully!');
      fetchPartnerships();
      fetchAvailablePartners();
    } catch (error) {
      console.error('Error removing partnership:', error);
      alert('Error removing partnership');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-600">
            Manage partnerships with other companies to enable warehouse inventory sharing.
            Only partnered companies can share inventory items with each other.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/20"
        >
          <Plus className="h-4 w-4 mr-2" />
          Manage Partnerships
        </button>
      </div>

      {/* Current Partnerships Overview */}
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Current Partnerships</h3>
        {loading ? (
          <div className="text-center text-slate-500 py-8">Loading partnerships...</div>
        ) : partnerships.length === 0 ? (
          <div className="text-center text-slate-500 py-8 border-2 border-dashed border-brand-200 rounded-xl">
            <Users className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Partnerships</h3>
            <p className="text-slate-600 mb-4">
              You don't have any company partnerships established yet.
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/20"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Partnership
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {partnerships.map(partnership => (
              <div key={partnership.id} className="bg-steel-50 rounded-xl p-4 border border-brand-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-slate-900">{partnership.name}</h4>
                  <span className="text-xs px-2 py-1 bg-brand-100 text-brand-800 rounded-full">
                    {partnership.access_level?.replace('_', ' ') || 'Full Access'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Active Partnership</span>
                  <button
                    onClick={() => removePartnership(partnership.id, partnership.name)}
                    className="text-red-600 hover:text-red-900 text-sm"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Add Partnership */}
      {availablePartners.length > 0 && (
        <div className="bg-brand-50 rounded-xl p-4 border border-brand-200">
          <h3 className="text-md font-medium text-slate-800 mb-3">Quick Add Partnership</h3>
          <div className="flex items-center space-x-3">
            <select
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target.value)}
              className="flex-1 px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
              disabled={creating}
            >
              <option value="">Select a company...</option>
              {availablePartners.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
            <button
              onClick={createPartnership}
              disabled={!selectedPartner || creating}
              className="px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:bg-steel-400 disabled:cursor-not-allowed shadow-lg shadow-brand-500/20"
            >
              {creating ? 'Adding...' : 'Add Partnership'}
            </button>
          </div>
        </div>
      )}

      {/* Partnership Modal */}
      {showModal && (
        <CompanyPartnershipsModal 
          onClose={() => {
            setShowModal(false);
            fetchPartnerships();
            fetchAvailablePartners();
          }}
        />
      )}
    </div>
  );
};

export default WarehouseSettings;